import { useContactInvalidation } from "@/hooks/use-contact-invalidation";
import {
  createContactEmailV1,
  deleteContactEmailV1,
  updateContactEmailV1,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError, type APIResponse, type Contact } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { orderBy } from "lodash";
import toast from "react-hot-toast";
import { type ContactSubitemMutationOptions } from "./contact-subitem-mutation-options";

export const useCreateContactEmail = (
  options?: ContactSubitemMutationOptions,
) => {
  const queryClient = useQueryClient();
  const invalidateContact = useContactInvalidation();
  return useMutation({
    mutationFn: (data: Parameters<typeof createContactEmailV1>[0]) =>
      createContactEmailV1(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData<APIResponse<Contact>>([
        queryKeys.contact,
        data.contactId,
      ]);
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.contact, data.contactId],
        (oldData: APIResponse<Contact> | undefined) => {
          if (!oldData) return;
          const oldEmails = oldData.data.emails;
          return {
            data: {
              ...oldData.data,
              emails: [
                ...oldEmails.map((email) =>
                  data.isPrimary ? { ...email, isPrimary: false } : email,
                ),
                data,
              ],
            },
          };
        },
      );

      return { previousData };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.contact, variables.contactId],
          context.previousData,
        );
      }
      toast.error("Error creating contact email");
    },
    onSettled: async () => {
      if (options?.invalidateOnSettled !== false) {
        await invalidateContact();
      }
    },
    onSuccess: () => {
      if (options?.showSuccessToast !== false) {
        toast.success("Contact email created successfully");
      }
    },
  });
};

export const useUpdateContactEmail = (
  options?: ContactSubitemMutationOptions,
) => {
  const queryClient = useQueryClient();
  const invalidateContact = useContactInvalidation();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateContactEmailV1>[0]) =>
      updateContactEmailV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.contact, data.contactId],
        (oldData: APIResponse<Contact> | undefined) => {
          if (!oldData) return;
          const oldEmails = oldData.data.emails;
          if (data.isPrimary) {
            oldEmails.forEach((email) => {
              if (email.id !== data.id) {
                email.isPrimary = false;
              }
            });
          }
          return {
            data: {
              ...oldData.data,
              emails: orderBy(
                oldEmails.map((email) => {
                  return email.id === data.id ? data : email;
                }),
                ["isPrimary"],
                ["desc"],
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([
          queryKeys.contact,
          data.contactId,
        ]),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.contact, variables.contactId],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating contact email");
    },

    onSettled: async () => {
      if (options?.invalidateOnSettled !== false) {
        await invalidateContact();
      }
    },
    onSuccess: () => {
      if (options?.showSuccessToast !== false) {
        toast.success("Contact email updated successfully");
      }
    },
  });
};

export const useDeleteContactEmail = (
  options?: ContactSubitemMutationOptions,
) => {
  const queryClient = useQueryClient();
  const invalidateContact = useContactInvalidation();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteContactEmailV1>[0]) =>
      deleteContactEmailV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.contact, data.contactId],
        (oldData: APIResponse<Contact> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              emails: oldData.data.emails.filter((email) => {
                return email.id !== data.id;
              }),
              ...data,
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([
          queryKeys.contact,
          data.contactId,
        ]),
      };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.contact, variables.contactId],
          context.previousData,
        );
      }
      toast.error("Error deleting contact email");
    },

    onSettled: async () => {
      if (options?.invalidateOnSettled !== false) {
        await invalidateContact();
      }
    },
    onSuccess: async (_result, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.contactPortalAccess, variables.contactId],
      });
      if (options?.showSuccessToast !== false) {
        toast.success("Contact email deleted successfully");
      }
    },
  });
};
